import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { Provider } from 'urql';
import { OpenFeature } from '@openfeature/react-sdk';
import * as amplitude from '@amplitude/analytics-browser';

import { http } from '@/utils/http';
import { type Children } from '@/utils/types';
import { AMPLITUDE_API_KEY, PLATFORM_API_PATH } from '@/utils/constants';
import { gqlClient } from '@/utils/gql';

interface User {
  avatar: string | null;
  company: {
    id: number;
    name: string;
  };
  email: string;
  id: number;
  name: string | null;
  roles: string[];
}

interface AuthProviderState {
  clearUser: () => void;
  setUser: () => void;
  user: User | null;
}

const initialState: AuthProviderState = {
  clearUser: () => null,
  setUser: () => null,
  user: null,
};

const AuthContext = createContext<AuthProviderState>(initialState);

export function AuthProvider({ children }: Children) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const hasAuth = useCallback(async () => {
    try {
      const { data, status } = await http.get('/v2/auth/me', {
        baseURL: PLATFORM_API_PATH,
      });

      if (status === 200) {
        const userId = data.id.toString();

        setUser(data);
        await OpenFeature.setContext({
          company_id: data.company?.id,
          email: data.email,
          targetingKey: userId,
          user_id: userId,
          role: data.roles[0],
        });

        if (AMPLITUDE_API_KEY) {
          amplitude.init(AMPLITUDE_API_KEY, {
            defaultTracking: false,
          });

          const identifyEvent = new amplitude.Identify();

          identifyEvent
            .set('name', data.name)
            .set('email', data.email)
            .set('account', data.company?.name);
          amplitude.identify(identifyEvent);

          // Amplitude user IDs must be at least 5 characters
          const amplitudeId = data.id.toString().padStart(5, '0');
          amplitude.setUserId(amplitudeId);
        }

        return true;
      } else {
        await OpenFeature.clearContext();
        return false;
      }
    } catch (error) {
      await OpenFeature.clearContext();
      return false;
    }
  }, []);

  const value = useMemo(
    () => ({
      clearUser: () => {
        setUser(null);
        navigate('/logout');
      },
      setUser: hasAuth,
      user,
    }),
    [hasAuth, navigate, user],
  );

  useEffect(() => {
    async function checkForAuth() {
      const auth = await hasAuth();
      setLoading(false);

      if (!auth) {
        setUser(null);
      }
    }

    if (!user) {
      checkForAuth();
    }
  }, [hasAuth, user]);

  return (
    <AuthContext value={value}>
      <Provider value={gqlClient}>{loading ? null : children}</Provider>
    </AuthContext>
  );
}

export function useUser() {
  const context = use(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
